import type { NostrEvent } from '@nostrify/nostrify';
import { sanitizeUrl } from './sanitizeUrl';

/**
 * NIP-58 badges, read the same way Ditto reads them.
 *
 * A profile's accepted badges live in a "profile badges" event: kind 10008
 * (the current replaceable list) or the legacy addressable kind 30008 with
 * `d=profile_badges`. Each accepted badge is an `a` tag pointing at a kind
 * 30009 badge definition, which carries the name and artwork.
 */
export const BADGE_DEFINITION_KIND = 30009;
export const BADGE_PROFILE_KIND = 10008;
export const BADGE_PROFILE_KIND_LEGACY = 30008;

/** A reference to a badge definition, taken from a profile badges event. */
export interface BadgeRef {
  /** `30009:<pubkey>:<identifier>` */
  aTag: string;
  pubkey: string;
  identifier: string;
}

/** Parsed kind 30009 badge definition. */
export interface BadgeData {
  aTag: string;
  identifier: string;
  name: string;
  description?: string;
  image?: string;
  thumbs: { url: string; dimensions?: string }[];
}

function isHexId(value: unknown): value is string {
  return typeof value === 'string' && /^[0-9a-f]{64}$/.test(value);
}

/**
 * Kind 10008 is unambiguously a profile badges list. Kind 30008 only counts
 * when `d=profile_badges` — any other `d` is a NIP-51 badge *set*, which is a
 * curated group rather than the owner's showcase.
 */
export function isProfileBadgesEvent(event: NostrEvent): boolean {
  if (event.kind === BADGE_PROFILE_KIND) return true;
  if (event.kind !== BADGE_PROFILE_KIND_LEGACY) return false;
  return event.tags.find(([n]) => n === 'd')?.[1] === 'profile_badges';
}

/**
 * Parse the `a` tags of a profile badges event into badge references, in the
 * order the owner arranged them. References with a malformed coordinate are
 * dropped — a non-hex pubkey would only produce a query that can never match.
 */
export function parseProfileBadges(event: NostrEvent): BadgeRef[] {
  if (!isProfileBadgesEvent(event)) return [];

  const refs: BadgeRef[] = [];
  const seen = new Set<string>();

  for (const [name, value] of event.tags) {
    if (name !== 'a' || !value) continue;
    const parts = value.split(':');
    if (parts.length < 3) continue;
    const [kind, pubkey] = parts;
    const identifier = parts.slice(2).join(':');
    if (Number(kind) !== BADGE_DEFINITION_KIND) continue;
    if (!isHexId(pubkey) || !identifier) continue;
    if (seen.has(value)) continue;
    seen.add(value);
    refs.push({ aTag: value, pubkey, identifier });
  }

  return refs;
}

/** Parse a kind 30009 badge definition. Returns null when it isn't usable. */
export function parseBadgeDefinition(event: NostrEvent): BadgeData | null {
  if (event.kind !== BADGE_DEFINITION_KIND) return null;

  const identifier = event.tags.find(([n]) => n === 'd')?.[1];
  if (!identifier) return null;

  const thumbs: BadgeData['thumbs'] = [];
  for (const tag of event.tags) {
    if (tag[0] !== 'thumb' || !tag[1]) continue;
    const url = sanitizeUrl(tag[1]);
    if (url) thumbs.push({ url, dimensions: tag[2] });
  }

  return {
    aTag: `${BADGE_DEFINITION_KIND}:${event.pubkey}:${identifier}`,
    identifier,
    name: event.tags.find(([n]) => n === 'name')?.[1] || identifier,
    description: event.tags.find(([n]) => n === 'description')?.[1],
    image: sanitizeUrl(event.tags.find(([n]) => n === 'image')?.[1]),
    thumbs,
  };
}

function parseDimension(dimensions?: string): number {
  const match = dimensions?.match(/^(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
}

/**
 * Smallest thumbnail that still meets the target size, falling back to the
 * largest one available and finally to the full image.
 */
export function badgeImageUrl(badge: BadgeData, targetSize: number): string | undefined {
  const sorted = [...badge.thumbs].sort(
    (a, b) => parseDimension(a.dimensions) - parseDimension(b.dimensions),
  );
  for (const thumb of sorted) {
    if (parseDimension(thumb.dimensions) >= targetSize) return thumb.url;
  }
  return sorted[sorted.length - 1]?.url ?? badge.image;
}
